import { useCallback } from 'react';
import { supabase } from '../../lib/supabase-client';
import { Property } from '../../types/property';
import { toast } from 'sonner';

export function usePhotos(
    properties: Property[],
    setProperties: React.Dispatch<React.SetStateAction<Property[]>>,
    userId?: string
) {

    const handleRestorePhoto = useCallback(async (photoUrl: string) => {
        let imageId: string | undefined;
        let propertyId: string | undefined;

        for (const p of properties) {
            const img = p.galleryImages.find(i => i.url === photoUrl || i.originalUrl === photoUrl);
            if (img) {
                imageId = img?.id;
                propertyId = p.id;
                break;
            }
        }

        if (imageId && propertyId) {
            const { error } = await supabase
                .from('images')
                .update({ is_deleted: false })
                .eq('id', imageId);

            if (error) {
                console.error('Error restoring photo:', error);
                toast.error('Nie udało się przywrócić zdjęcia');
                return;
            }

            setProperties(prev => prev.map(p => {
                if (p.id === propertyId) {
                    return {
                        ...p,
                        galleryImages: p.galleryImages.map(img =>
                            (img.url === photoUrl || img.originalUrl === photoUrl)
                                ? { ...img, deleted: false }
                                : img
                        )
                    };
                }
                return p;
            }));

            toast.success('Zdjęcie przywrócone');
        }
    }, [properties, setProperties]);

    const handleDeletePhotoFromGallery = useCallback(async (propertyId: string, photoUrl: string) => {
        const property = properties.find(p => p.id === propertyId);
        const img = property?.galleryImages?.find(i => i.url === photoUrl || i.originalUrl === photoUrl);

        const imageId = img?.id;

        if (imageId) {
            const { error } = await supabase
                .from('images')
                .update({ is_deleted: true })
                .eq('id', imageId);

            if (error) {
                console.error('Error deleting photo:', error);
                toast.error('Nie udało się usunąć zdjęcia');
                return;
            }
        }

        setProperties((prev) => {
            const next = prev.map((p) => {
                if (p.id === propertyId) {
                    const newImages = p.galleryImages.map((img) =>
                        (img.url === photoUrl || img.originalUrl === photoUrl)
                            ? { ...img, deleted: true }
                            : img
                    );
                    return { ...p, galleryImages: newImages };
                }
                return p;
            });
            return next;
        });

        toast.success('Plik przeniesiony do kosza', {
            action: {
                label: 'Cofnij',
                onClick: () => handleRestorePhoto(photoUrl)
            }
        });
    }, [properties, setProperties, handleRestorePhoto]);

    const handleToggleFavorite = useCallback(async (photoUrl: string) => {
        let imageId: string | undefined;
        let currentFavStatus = false;
        let propertyId: string | undefined;

        for (const p of properties) {
            const img = p.galleryImages.find(i => i.url === photoUrl || i.originalUrl === photoUrl);
            if (img) {
                imageId = img?.id;
                currentFavStatus = img.favorite;
                propertyId = p.id;
                break;
            }
        }

        if (imageId && propertyId) {
            const newStatus = !currentFavStatus;
            const { error } = await supabase
                .from('images')
                .update({ is_favorite: newStatus })
                .eq('id', imageId);

            if (error) {
                console.error('Error updating favorite:', error);
                return;
            }

            setProperties(prev => prev.map(p => {
                if (p.id === propertyId) {
                    return {
                        ...p,
                        galleryImages: p.galleryImages.map(img =>
                            (img.url === photoUrl || img.originalUrl === photoUrl)
                                ? { ...img, favorite: newStatus }
                                : img
                        )
                    };
                }
                return p;
            }));
        }
    }, [properties, setProperties]);

    const handleMovePhoto = useCallback(async (photoUrl: string, targetPropertyId: string, currentRoomName: string) => {
        let sourcePropertyId: string | undefined;
        let imageId: string | undefined;

        // Find source
        for (const p of properties) {
            const img = p.galleryImages.find(i => i.roomName === currentRoomName && (i.originalUrl === photoUrl || i.url === photoUrl));
            if (img) {
                sourcePropertyId = p.id;
                imageId = img?.id;
                break;
            }
        }

        if (imageId && sourcePropertyId) {
            const { error } = await supabase
                .from('images')
                .update({ property_id: targetPropertyId })
                .eq('id', imageId);

            if (error) {
                console.error('Error moving photo:', error);
                toast.error('Nie udało się przenieść zdjęcia');
                return;
            }

            // Update state
            setProperties(prevProperties => {
                const sourcePropIndex = prevProperties.findIndex(p => p.id === sourcePropertyId);
                const targetPropIndex = prevProperties.findIndex(p => p.id === targetPropertyId);

                if (sourcePropIndex === -1 || targetPropIndex === -1) return prevProperties;

                const newProperties = [...prevProperties];
                const sourceProperty = { ...newProperties[sourcePropIndex], galleryImages: [...newProperties[sourcePropIndex].galleryImages] };

                const imageIndex = sourceProperty.galleryImages.findIndex(img => img.id === imageId);
                if (imageIndex === -1) return prevProperties;

                const [movedImage] = sourceProperty.galleryImages.splice(imageIndex, 1);
                sourceProperty.photoCount = sourceProperty.galleryImages.length;
                newProperties[sourcePropIndex] = sourceProperty;

                const targetProp = { ...newProperties[targetPropIndex], galleryImages: [...(newProperties[targetPropIndex].galleryImages || [])] };
                targetProp.galleryImages.push(movedImage);
                targetProp.photoCount = targetProp.galleryImages.length;
                newProperties[targetPropIndex] = targetProp;

                return newProperties;
            });

            toast.success('Zdjęcie zostało przeniesione');
        }
    }, [properties, setProperties]);

    /**
     * Handles assignment of "unsorted" Quick Render photos to a property.
     * Inserts new records into 'images' and 'generation_outputs' tables.
     */
    const handleAssignUnsortedPhoto = useCallback(async (targetPropertyId: string, photoData: any) => {
        if (!userId) {
            console.error("No userId available to assign photo");
            toast.error("Błąd autoryzacji");
            return;
        }

        const { variants, originalUrl, roomName, renderSettings, originalGcsPath } = photoData;
        const toastId = toast.loading("Przypisywanie rendera do nieruchomości...");

        try {
            // 1. Insert "Original" Image
            const originalPath = originalGcsPath || `unsorted/${crypto.randomUUID()}_original.png`;

            const { data: insertedOriginal, error: originalError } = await supabase
                .from('images')
                .insert({
                    property_id: targetPropertyId,
                    user_id: userId,
                    image_type: 'original',
                    gcs_path: originalPath,
                    file_name: `original_${Date.now()}.png`,
                    file_size: 0,
                    mime_type: 'image/jpeg',
                    room_name: roomName,
                    is_favorite: false,
                    is_deleted: false
                })
                .select()
                .single();

            if (originalError) throw originalError;

            // 2. Insert Generation Record
            const { data: insertedGeneration, error: genError } = await supabase
                .from('generations')
                .insert({
                    property_id: targetPropertyId,
                    user_id: userId,
                    source_image_id: insertedOriginal.id,
                    generation_mode: renderSettings?.mode || 'enhance',
                    credits_used: 1
                } as any) // Cast to any to bypass incomplete TS type
                .select()
                .single();

            if (genError) throw genError;

            // 3. Insert Variant Images (Generated)
            const imagesToInsert = (variants as any[]).map(v => ({
                property_id: targetPropertyId,
                user_id: userId,
                image_type: 'generated',
                gcs_path: v.gcsPath || `unsorted/${crypto.randomUUID()}_variant.png`,
                file_name: `generated_${Date.now()}_${v.label}.png`,
                file_size: 0,
                mime_type: 'image/png',
                room_name: roomName,
                is_favorite: false,
                is_deleted: false
            }));

            const { data: insertedVariants, error: variantsError } = await supabase
                .from('images')
                .insert(imagesToInsert)
                .select();

            if (variantsError) throw variantsError;

            // 4. Link Generation to Outputs
            if (insertedVariants && insertedVariants.length > 0) {
                const outputsToInsert = insertedVariants.map((v, idx) => ({
                    generation_id: insertedGeneration.id,
                    output_image_id: v.id,
                    ai_provider: (variants as any[])[idx]?.provider || 'unknown',
                    prompt_used: (variants as any[])[idx]?.promptUsed || null,
                    generation_time_ms: (variants as any[])[idx]?.generationTimeMs || null
                }));

                const { error: outputsError } = await supabase
                    .from('generation_outputs')
                    .insert(outputsToInsert);

                if (outputsError) throw outputsError;

                // 5. Update State
                const newGalleryImage: any = {
                    id: insertedOriginal.id,
                    url: variants[0].url,
                    category: renderSettings?.mode || 'enhance',
                    aspectRatio: 'landscape',
                    variantCount: insertedVariants.length,
                    roomName: roomName,
                    originalUrl: originalUrl || variants[0].url,
                    deleted: false,
                    favorite: false,
                    variants: variants.map((v: any, i: number) => ({
                        ...v,
                        id: insertedVariants[i]?.id || v.id
                    })),
                    renderSettings: renderSettings
                };

                setProperties(prev => {
                    const idx = prev.findIndex(p => p.id === targetPropertyId);
                    if (idx === -1) return prev;

                    const updated = [...prev];
                    const targetProp = { ...updated[idx] };
                    targetProp.galleryImages = [newGalleryImage, ...(targetProp.galleryImages || [])];
                    targetProp.photoCount = targetProp.galleryImages.length;
                    updated[idx] = targetProp;
                    return updated;
                });

                toast.dismiss(toastId);
                toast.success("Render przypisany do nieruchomości!");
            }

        } catch (err) {
            console.error("Error assigning unsorted photo:", err);
            toast.dismiss(toastId);
            toast.error("Wystąpił błąd podczas zapisywania rendera.");
        }
    }, [userId, setProperties]);

    return {
        handleDeletePhotoFromGallery,
        handleRestorePhoto,
        handleToggleFavorite,
        handleMovePhoto,
        handleAssignUnsortedPhoto
    };
}
